import Loader from 'components/Loader/Loader';
import './B2BRewards.css';
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Dropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import User from 'utils/user.util';

const B2BRewards = ({ property }) => {
    const { t } = useTranslation();
    const currentYear = new Date().getFullYear().toString();
    const yearsToDisplay = () => {
        const years = [];
        const startYear = 2024;
        const currentYearNum = new Date().getFullYear();
        for (let year = startYear; year <= currentYearNum; year++) {
            years.push(year.toString());
        }
        return years;
    };
    const [year, setYear] = useState(currentYear);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);

    const fetchRewards = useCallback(async (propertyCode, year) => {
        setLoading(true);
        try {
            const token = User.getToken();
            const response = await fetch(
                `${process.env.REACT_APP_PROXY_URL || ''}/api/b2b_rewards/${propertyCode}/${year}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            let data = [];
            setLoading(false);

            if (!response.ok) {
                console.error('Failed to fetch rewards. Rewards response not ok.');
            } else {
                try {
                    data = await response.json();
                } catch (e) {
                    console.error('Error parsing rewards:', e);
                }
            }

            if (data?.error) {
                console.error('Error in rewards response:', data.error);
            }

            setLoading(false);
            setData(data);
        } catch (error) {
            setLoading(false);
            console.error('Error fetching rewards:', error);
        }
    }, []);

    const convertToCSV = (array) => {
        if (!array.length) {
            return '';
        }
        // Extract the keys (headers) from the first object
        const headers = Object.keys(array[0]);
        const csvRows = [];

        // Add the headers row
        csvRows.push(headers.join(','));

        // Loop over the array and create a row for each object
        for (const row of array) {
            // Map each header to the corresponding value in the row.
            // Also, wrap values in quotes in case they contain commas.
            const values = headers.map((header) => `"${row[header]}"`);
            csvRows.push(values.join(','));
        }

        return csvRows.join('\n');
    };

    // Function to trigger CSV download
    const downloadCSV = () => {
        const csvData = convertToCSV(data);
        // Create a blob with the CSV data and the correct MIME type
        const blob = new Blob([csvData], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);

        // Create a formatted date string (e.g., YYYY-MM-DD)
        const today = new Date();
        const date = today.toISOString().split('T')[0]; // "YYYY-MM-DD"

        // Create a temporary anchor element and trigger the download
        const a = document.createElement('a');
        a.setAttribute('hidden', '');
        a.setAttribute('href', url);
        a.setAttribute('download', `b2b_yearly_rewards_${date}.csv`);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    useEffect(() => {
        fetchRewards(property.external_id__c, year);
    }, [fetchRewards, property, year]);

    return (
        <div className="b2b-rewards">
            <h3>{t('b2b_yearly_rewards')}</h3>
            <Dropdown>
                <Dropdown.Toggle variant="secondary" id="dropdown-basic">
                    {year}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                    {yearsToDisplay().map((yr) => (
                        <Dropdown.Item
                            key={yr}
                            eventKey={yr}
                            active={year === yr}
                            onClick={() => setYear(yr)}
                        >
                            {yr}
                        </Dropdown.Item>
                    ))}
                </Dropdown.Menu>
            </Dropdown>
            {!loading && (
                <div className="download-csv">
                    <p>
                        {data.length || 0} {t('total_results')}
                    </p>
                    {data.length === 0 ? (
                        <></>
                    ) : (
                        <Button onClick={downloadCSV}>{t('download_csv')}</Button>
                    )}
                </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
                {!loading ? (
                    <table border="1" cellPadding="10">
                        <thead>
                            <tr>
                                <th>id</th>
                                <th>{t('company')}</th>
                                <th>{t('name')}</th>
                                <th>{t('email')}</th>
                                <th>{t('phone')}</th>
                                <th>{t('language')}</th>
                                <th>{t('total_purchases')}</th>
                                <th>{t('tier')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="spacer">
                                <td colSpan="8"></td>
                            </tr>
                            {data.length === 0 ? (
                                <tr>
                                    <td>{t('no_results')}</td>
                                </tr>
                            ) : (
                                data.map((item) => (
                                    <tr key={item.id}>
                                        <td>{item.id}</td>
                                        <td>{item.company_name}</td>
                                        <td>{item.name}</td>
                                        <td>{item.email}</td>
                                        <td>{item.phone}</td>
                                        <td>{item.language}</td>
                                        <td>{item.total_purchases}</td>
                                        <td>{item.tier}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                ) : (
                    <div>
                        {t('fetching_rewards')}
                        <Loader color="dark" />
                    </div>
                )}
            </div>
        </div>
    );
};

export default B2BRewards;
